import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import api from "../../../services/api";
import { useTranslation } from 'react-i18next';

import { Col, Row } from 'react-bootstrap';


import { HRHistogramChart } from './HRHistogramChart'
import { RRHistogramChart } from './RRHistogramChart'
import { ScatterChart } from './ScatterChart'
import { HRSpecterChart } from './HRSpecterChart'
import { RRSpecterChart } from './RRSpecterChart'
import { AverageIntervalChart } from './AverageIntervalChart'

import { AllProcessedDataSlider } from './AllProcessedDataSlider'
import { AllProcessedDataValues } from './AllProcessedDataValues'

import Loading from '../Loading'

import sharedStyles from './AllProcessedData.module.scss'
import styles from './AllProcessedDataSmallCharts.module.scss'
export const AllProcessedDataSmallCharts = forwardRef((props, ref) => {
  const lastKnownProcessedTime = useRef(null);
  const debounceTimerRef = useRef(null);

  useImperativeHandle(ref, () => {
    return {
      setLastProcessedTime(lastProcessedTime) {
        // Only update if the processed time has actually changed and is valid
        if (lastProcessedTime && 
            lastProcessedTime > 0 && 
            lastProcessedTime !== lastKnownProcessedTime.current) {
          lastKnownProcessedTime.current = lastProcessedTime;
          let isMaxValueSelected = sliderRef.current?.isCurrendSliderMaxRight();
          sliderRef.current?.setLastProcessedTime(lastProcessedTime);
          if (isMaxValueSelected)
            sliderRef.current?.setSliderValue(lastProcessedTime);
        }
      }
    };
  }, []);

  const sliderRef = useRef(null);
  const { t } = useTranslation();

  const [dataCharts, setDataCharts] = useState([]);
  const [singleValues, setSingleValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [initialMaxTimeSeconds] = useState(props.totalRecordLengthSeconds);
  const ChartDataTypes = {
    //EcgWithRrIntervals: 10,
    //HR: 20,
    HRHistogram: 30,
    //RR: 40,
    RRHistogram: 50,
    //SI: 60,
    Scatter: 70,
    HRSpecter: 80,
    RRSpecter: 90,
    EcgAverageInterval: 100
  }

  useEffect(() => {
    //setMaxTimeSeconds(props.totalRecordLengthSeconds);
    if (props.totalRecordLengthSeconds && props.totalRecordLengthSeconds > 0) {
      onSliderTimeChange(props.totalRecordLengthSeconds);
    }
  }, [props.totalRecordLengthSeconds]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);


  const onSliderTimeChange = (newTime) => {
    // Don't make API call if newTime is null or undefined
    if (newTime == null || newTime === undefined) {
      return;
    }
    
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Debounce the API call to prevent rapid requests
    debounceTimerRef.current = setTimeout(() => {
      // The backend always expects time from 0, regardless of timeViewMode
      // timeViewMode only affects the display in the UI
      setIsLoading(true);

      api.post(`${props.requestUrl}/window?endTimeSec=${newTime}`)
        .then((response) => {

          if (response.data.chartList && response.data.values) {
            setDataCharts(response.data.chartList);
            setSingleValues(response.data.values);
          }
          setIsLoading(false);
        }, (error) => {
          setIsLoading(false);
        });
    }, 300); // 300ms debounce
  };

  const getDataByType = (chartType) => dataCharts.find(x => x.chartType === chartType);

  // Reusable ChartContainer component that handles loading state
  const ChartContainer = ({ children }) => {
    return (
      <Col className={`${sharedStyles.oneChart} ${styles.chartContainer}`}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Loading />
          </div>
        ) : (
          children
        )}
      </Col>
    );
  };

  const HRHistogramComponent = () => {
    let x = getDataByType(ChartDataTypes.HRHistogram);
    return (
      <>
        {x && <HRHistogramChart data={x.dataColumns} displayName={x.displayName} plotLinesX={x.plotLinesX} />}
      </>
    );
  }

  const RRHistogramComponent = () => {
    let x = getDataByType(ChartDataTypes.RRHistogram);
    return (
      <>
        {x && <RRHistogramChart data={x.dataColumns} displayName={x.displayName} plotLinesX={x.plotLinesX} />}
      </>
    );
  }

  const ScatterComponent = () => {
    let x = getDataByType(ChartDataTypes.Scatter);
    return (
      <>
        {x && <ScatterChart data={x.data} displayName={x.displayName} plotLinesX={x.plotLinesX} />}
      </>
    );
  }

  const HRSpecter = () => {
    let x = getDataByType(ChartDataTypes.HRSpecter);
    return (
      <>
        {x && <HRSpecterChart data={x.data} displayName={x.displayName} plotLinesX={x.plotLinesX} />}
      </>
    );
  }

  const RRSpecter = () => {
    let x = getDataByType(ChartDataTypes.RRSpecter);
    return (
      <>
        {x && <RRSpecterChart data={x.data} displayName={x.displayName} plotLinesX={x.plotLinesX} />}
      </>
    );
  }

  const EcgAverageInterval = () => {
    let x = getDataByType(ChartDataTypes.EcgAverageInterval);
    return (
      <>
        {x && <AverageIntervalChart data={x.data} displayName={x.displayName} markers={x.markers} />}
      </>
    );
  }

  // Check if charts have data
  const hasChartData = dataCharts && dataCharts.length > 0;
  
  return (
    <>
      <h2 className="h2">{t('recordView.processedData.headerPart')}</h2>

      <AllProcessedDataValues 
        values={singleValues} 
        isDetailed={true} 
        timeViewMode={props.timeViewMode}
        recordStartTime={props.recordStartTime}
        isLoading={isLoading}
      />

      <Row className="pt-5"><p>{t('recordView.processedData.selectTimeRange')}</p></Row>
      <Row className="py-3">
        <Col>
          <AllProcessedDataSlider 
            ref={sliderRef} 
            minTimeSec={0} 
            maxTimeSec={initialMaxTimeSeconds} 
            onTimeChangedCallback={onSliderTimeChange}
            timeViewMode={props.timeViewMode}
            recordStartTime={props.recordStartTime}
          />
        </Col>
      </Row>

      {!hasChartData && !isLoading && (
        <Row className="py-3">
          <Col className={styles.noDataMessage}>
            <em>{t('recordView.processedData.values.notEnoughData')}</em>
          </Col>
        </Row>
      )}

      {(hasChartData || isLoading) && (
        <>
          <Row className="py-3">
            <ChartContainer>
              <HRHistogramComponent />
            </ChartContainer>
            <ChartContainer>
              <RRHistogramComponent />
            </ChartContainer>
            <ChartContainer>
              <ScatterComponent />
            </ChartContainer>
          </Row>

          <Row className="py-3">
            <ChartContainer>
              <HRSpecter />
            </ChartContainer>
            <ChartContainer>
              <RRSpecter />
            </ChartContainer>
            <ChartContainer>
              <EcgAverageInterval />
            </ChartContainer>
          </Row>
        </>
      )}

      <Row>

      </Row>

    </>
  );
});